import { applyInputBudget } from '../budget.js';
import { OPERATION_CONTRACTS, PromptCompileError } from '../contracts.js';
import { buildProvenanceManifest } from '../manifest.js';
import type {
  ContextTier,
  FallbackPolicy,
  PromptPurpose,
  PromptRequest,
  PromptSourceBundle,
  ProviderKind,
  TokenBudget,
} from '../types.js';
import { PROMPT_SCHEMA_VERSION } from '../types.js';
import { buildWorldContext, filterActiveDevices, normalizeActors } from '../filters.js';

/** Purposes that require PromptSourceBundle.connected (C2 Q1 — fail-fast). */
export const CONNECTED_REQUIRED_PURPOSES: ReadonlySet<PromptPurpose> = new Set([
  'world_devices',
  'character_options',
  'runtime_scene',
]);

export function assertConnectedContext(source: PromptSourceBundle, purpose: PromptPurpose): void {
  if (!CONNECTED_REQUIRED_PURPOSES.has(purpose)) return;
  const c = source.connected;
  if (!c?.locationType?.trim() && !c?.description?.trim()) {
    throw new PromptCompileError(
      `${purpose} requires PromptSourceBundle.connected with locationType and/or description`,
    );
  }
}

/** Reject tier sections above the compiler max — Tier-1 calls cannot carry Tier-2/3 fields. */
export function assertTierCap(maxTier: ContextTier, sections: CompileSections): void {
  if (maxTier < 1 && sections.tier1?.trim()) {
    throw new PromptCompileError('Tier-1 sections forbidden when maxTier is 0');
  }
  if (maxTier < 2 && sections.tier2?.trim()) {
    throw new PromptCompileError('Tier-2 sections forbidden when maxTier is 1 or below');
  }
  if (maxTier < 3 && sections.tier3?.trim()) {
    throw new PromptCompileError('Tier-3 sections forbidden when maxTier is 2 or below');
  }
}

export interface CompilerSpec {
  purpose: PromptPurpose;
  maxTier: ContextTier;
  allowedTiers: ContextTier[];
  budget: TokenBudget;
  provider: ProviderKind;
}

export interface CompileSections {
  tier0: string;
  tier1?: string;
  tier2?: string;
  tier3?: string;
}

export function defaultConstraints(allowAssumption: boolean) {
  return {
    contentBoundary: 'edgy-but-safe' as const,
    forbiddenFields: ['apiKey', 'fullLastName', 'rawProviderPayload', 'rawPrompt'],
    knownFacts: [],
    confirmedDecisions: [],
    allowAssumption,
  };
}

export function assembleInstructions(
  sections: CompileSections,
  maxTier: ContextTier,
  budget: TokenBudget,
): { instructions: string; tierUsed: ContextTier; truncated: boolean } {
  const parts: Array<{ key: string; text: string; priority: number }> = [];
  if (sections.tier0) parts.push({ key: 'tier0', text: sections.tier0, priority: 0 });
  if (maxTier >= 1 && sections.tier1) parts.push({ key: 'tier1', text: sections.tier1, priority: 1 });
  if (maxTier >= 2 && sections.tier2) parts.push({ key: 'tier2', text: sections.tier2, priority: 2 });
  if (maxTier >= 3 && sections.tier3) parts.push({ key: 'tier3', text: sections.tier3, priority: 3 });

  const budgeted = applyInputBudget(parts, budget.inputTokens);
  let tierUsed: ContextTier = 0;
  if (maxTier >= 3 && sections.tier3 && !budgeted.omittedSections.includes('tier3')) tierUsed = 3;
  else if (maxTier >= 2 && sections.tier2 && !budgeted.omittedSections.includes('tier2')) tierUsed = 2;
  else if (maxTier >= 1 && sections.tier1 && !budgeted.omittedSections.includes('tier1')) tierUsed = 1;

  return { instructions: budgeted.text, tierUsed, truncated: budgeted.truncated };
}

export function baseRequest(
  source: PromptSourceBundle,
  spec: CompilerSpec,
  sections: CompileSections,
  actorIds: string[],
  eventIds: string[],
  summaryIds: string[],
  instructionHeader: string,
  fallback: FallbackPolicy<unknown>,
  extra?: Partial<PromptRequest>,
): PromptRequest {
  assertConnectedContext(source, spec.purpose);
  assertTierCap(spec.maxTier, sections);
  const world = buildWorldContext(source);
  const { instructions: body, tierUsed } = assembleInstructions(sections, spec.maxTier, spec.budget);
  const instructions = body ? `${instructionHeader}\n\n${body}` : instructionHeader;
  const contract = OPERATION_CONTRACTS[spec.purpose];

  const actors = normalizeActors(source.allActors.filter((a) => actorIds.includes(a.actorId)));
  const provenance = buildProvenanceManifest({
    purpose: spec.purpose,
    world,
    actorIds,
    actors,
    deviceIds: filterActiveDevices(source),
    eventIds,
    summaryIds,
    provider: spec.provider,
    fallbackUsed: false,
    validationStatus: 'pending',
    contextTierMax: spec.maxTier,
    contextTierUsed: tierUsed,
  });

  return {
    meta: {
      purpose: spec.purpose,
      schemaVersion: PROMPT_SCHEMA_VERSION,
      worldId: source.worldId,
      locale: source.locale ?? 'en-US',
      episodeId: source.episodeId,
      characterIds: actorIds.length ? actorIds : undefined,
    },
    world,
    simulation: {
      inWorldIso: source.clock.inWorldIso,
      episodeNumber: source.clock.episodeNumber,
      operatingStatus: source.operatingStatus,
      mode: source.mode,
      activeDeviceIds: filterActiveDevices(source),
      recentEventIds: eventIds,
      generationSeed: source.generationSeed,
    },
    actors,
    history: {
      compactSummaries: source.summaries.filter((s) => summaryIds.includes(s.summaryId)),
      recentEvents: source.events.filter((e) => eventIds.includes(e.eventId)),
    },
    constraints: defaultConstraints(spec.purpose === 'assumption'),
    allowedTiers: spec.allowedTiers,
    maxTier: spec.maxTier,
    budget: spec.budget,
    fallback,
    provenance,
    outputSchemaId: contract.outputSchemaId,
    persistencePolicy: contract.persistencePolicy,
    structuredOutput: contract.structuredOutput,
    instructions,
    ...extra,
  };
}
